import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Pagination } from '@/components/Pagination';
import { Snackbar } from '@/components/Snackbar';
import { StatusLabel } from '@/components/StatusLabel';
import { Table, TableColumn } from '@/components/Table';
import { AdminClaimDetailsModal } from '@/components/admin/AdminClaimDetailsModal';
import { BulkActionBar } from '@/components/admin/BulkActionBar';
import { BulkApproveModal } from '@/components/admin/BulkApproveModal';
import { ClaimStatusDropdown } from '@/components/admin/ClaimStatusDropdown';
import { ClaimsFilters } from '@/components/admin/ClaimsFilters';
import { ConfirmChangeModal } from '@/components/admin/ConfirmChangeModal';
import { ExportClaimsModal } from '@/components/admin/ExportClaimsModal';

import {
  ADMIN_STATUS_OPTIONS,
  ADMIN_STATUS_TRANSITIONS,
  BULK_STATUS_TRANSITIONS,
  getEffectiveStatusKey,
  resolveStatusOption,
} from '@/constants/adminStatus';
import { categories } from '@/constants/categories';

import { useAdminClaims } from '@/hooks/useAdminClaims';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { useUpdateClaimStatus } from '@/hooks/useUpdateClaimStatus';
import { useUserTypes } from '@/hooks/useUserTypes';

import { getReceiptDownloadUrl } from '@/services/admin';

import { loadAiNoteMarkdown } from '@/utils/aiNotes';

function claimRowKey(claim: { userEmail: string; id: string }) {
  return `${claim.userEmail}-${claim.id}`;
}

function generateMonthOptions(count: number): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

/**
 * Absent bounds mean "every month" to the backend, so the param cannot express "no month
 * selected". `noneSelected` carries that state instead.
 *
 * It rides in the same object as the bounds on purpose. Debouncing the bounds and a separate
 * flag lets their timers resolve in different renders, and the query then fires during the
 * intermediate combination with no bounds, returning every claim.
 */
function monthsToDates(months: string[]): {
  startDate?: string;
  endDate?: string;
  noneSelected: boolean;
} {
  if (months.length === 0) return { noneSelected: true };
  const sorted = [...months].sort();
  const startDate = `${sorted[0]}-01`;
  const [endYear, endMonth] = sorted[sorted.length - 1].split('-').map(Number);
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  const endDate = `${sorted[sorted.length - 1]}-${String(lastDay).padStart(2, '0')}`;
  return { startDate, endDate, noneSelected: false };
}

const MONTH_OPTIONS = generateMonthOptions(12);

function getValidStatusTransitions(status: string, flag?: 'yellow' | 'red' | null): string[] {
  const key = getEffectiveStatusKey(status, flag);
  return ADMIN_STATUS_TRANSITIONS[key] || [];
}

/** Selection is unrestricted (any status can be checked for export), but a bulk status change
 * should never even attempt claims that are obviously ineligible - filter here instead of letting
 * the backend reject them one-by-one and surface noisy per-claim errors for the obvious cases. */
function eligibleForTarget<
  T extends { id: string; userEmail: string; status: string; flag?: 'yellow' | 'red' | null },
>(claims: T[], target: string): { claimId: string; userEmail: string }[] {
  return claims
    .filter((c) =>
      BULK_STATUS_TRANSITIONS[target]?.includes(getEffectiveStatusKey(c.status, c.flag)),
    )
    .map((c) => ({ claimId: c.id, userEmail: c.userEmail }));
}

export function ClaimsTab() {
  const { user } = useAuth();
  const updateClaimStatusMutation = useUpdateClaimStatus();
  const { userTypes } = useUserTypes();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
    'submitted',
    'pending_yellow',
    'pending_red',
    'pre_approved',
    'approved',
    'pending_payment',
    'paid',
    'rejected',
  ]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([MONTH_OPTIONS[0].value]);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);
  const [pageSize, setPageSize] = useState(25);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [excludedGroups, setExcludedGroups] = useState<Set<string>>(new Set());
  const [nameQuery, setNameQuery] = useState('');
  const selectedGroupValues = useMemo(
    () => userTypes.filter((g) => !excludedGroups.has(g.value)).map((g) => g.value),
    [userTypes, excludedGroups],
  );
  const [openStatusDropdownId, setOpenStatusDropdownId] = useState<string | null>(null);
  const [statusChangeModal, setStatusChangeModal] = useState<{
    claimId: string;
    userEmail: string;
    currentStatus: string;
    newStatus: string;
    newStatusKey: string;
    newFlag: 'yellow' | 'red' | null;
  } | null>(null);
  const [loadingReceipts, setLoadingReceipts] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    message: '',
    type: 'error',
  });
  const [activeClaimId, setActiveClaimId] = useState<string | null>(null);
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());
  const [bulkStatusTarget, setBulkStatusTarget] = useState<string | null>(null);
  const [selectedTargetStatus, setSelectedTargetStatus] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMode, setExportMode] = useState<'selected' | 'all' | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const groupDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const exclusiveStartKey = cursorHistory[currentPage - 1] || null;
  const encodedCursor = exclusiveStartKey ? encodeURIComponent(exclusiveStartKey) : undefined;

  const normalizedStatuses = [
    ...new Set(
      selectedStatuses.map((s) => (s === 'pending_yellow' || s === 'pending_red' ? 'pending' : s)),
    ),
  ];
  /**
   * `null` means every status is deselected, which no claim can match and the param cannot
   * express: an absent status param reads as "every status" to the backend.
   */
  const statusParam = normalizedStatuses.length > 0 ? normalizedStatuses.join(',') : null;

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setCursorHistory([null]);
  }, []);

  const dateBounds = useMemo(() => monthsToDates(selectedMonths), [selectedMonths]);

  const hasYellow = selectedStatuses.includes('pending_yellow');
  const hasRed = selectedStatuses.includes('pending_red');
  const flagParam = [hasYellow && 'yellow', hasRed && 'red'].filter(Boolean).join(',') || undefined;

  /**
   * `undefined` sends no userType param, which the backend reads as "every group". `null` means
   * every group is deselected, which no claim can match and the param cannot express.
   *
   * Both states live in one debounced value on purpose. Debouncing the param and a separate
   * "none selected" flag lets their timers resolve in different renders, and the query then
   * fires during the intermediate combination with an empty userType, returning every claim.
   */
  const groupParam = useMemo<string | null | undefined>(() => {
    // userTypes is empty until the options load, which is not a deselection.
    if (userTypes.length === 0) return undefined;
    if (selectedGroupValues.length === 0) return null;
    if (selectedGroupValues.length === userTypes.length) return undefined;
    return selectedGroupValues.join(',');
  }, [userTypes.length, selectedGroupValues]);

  const debouncedStatusParam = useDebounce(statusParam, 500);
  const debouncedDateBounds = useDebounce(dateBounds, 500);
  const debouncedFlagParam = useDebounce(flagParam, 500);
  const debouncedGroupParam = useDebounce(groupParam, 500);
  /** A filter with every option deselected matches no claim, so skip the request entirely. */
  const filtersMatchNothing =
    debouncedGroupParam === null ||
    debouncedDateBounds.noneSelected ||
    debouncedStatusParam === null;

  useEffect(() => {
    void loadAiNoteMarkdown();
  }, []);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    resetPagination();
  }, [
    debouncedDateBounds.startDate,
    debouncedDateBounds.endDate,
    debouncedStatusParam,
    debouncedFlagParam,
    debouncedGroupParam,
    nameQuery,
    sortOrder,
    pageSize,
    resetPagination,
  ]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setSelectedClaimIds(new Set());
  }, [
    currentPage,
    debouncedStatusParam,
    debouncedFlagParam,
    debouncedDateBounds.startDate,
    debouncedDateBounds.endDate,
    sortOrder,
  ]);

  const {
    data: claimsQueryData,
    loading,
    refetching,
    error,
  } = useAdminClaims(
    {
      limit: pageSize,
      exclusiveStartKey: encodedCursor,
      status: debouncedStatusParam ?? undefined,
      flag: debouncedFlagParam,
      userType: debouncedGroupParam ?? undefined,
      name: nameQuery || undefined,
      startDate: debouncedDateBounds.startDate,
      endDate: debouncedDateBounds.endDate,
      sortOrder,
    },
    { enabled: !filtersMatchNothing },
  );

  /** With nothing selected the query params match the unfiltered "everything" request, so
   * react-query still serves that request's cached response here. Ignore it rather than trust
   * a disabled query to hold no data, which would show every claim and page through them. */
  const data = filtersMatchNothing ? null : claimsQueryData;

  const claims = useMemo(() => data?.items ?? [], [data?.items]);

  const exportFilterParams = useMemo(
    () => ({
      status: debouncedStatusParam ?? undefined,
      flag: debouncedFlagParam,
      userType: debouncedGroupParam ?? undefined,
      name: nameQuery || undefined,
      startDate: debouncedDateBounds.startDate,
      endDate: debouncedDateBounds.endDate,
      sortOrder,
    }),
    [
      debouncedStatusParam,
      debouncedFlagParam,
      debouncedGroupParam,
      nameQuery,
      debouncedDateBounds,
      sortOrder,
    ],
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setSelectedClaimIds((prev) => {
      if (prev.size === 0) return prev;
      const presentKeys = new Set(claims.map(claimRowKey));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((key) => {
        if (presentKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [claims]);

  const activeClaimForDetails = useMemo(
    () => (activeClaimId ? (claims.find((c) => c.id === activeClaimId) ?? null) : null),
    [claims, activeClaimId],
  );

  useEffect(() => {
    if (currentPage === cursorHistory.length && data?.nextCursor) {
      // oxlint-disable-next-line react/set-state-in-effect
      setCursorHistory((prev) => {
        if (prev[prev.length - 1] === data.nextCursor) {
          return prev;
        }
        return [...prev, data.nextCursor];
      });
    }
  }, [data?.nextCursor, currentPage, cursorHistory.length]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target as Node)) {
        setShowMonthDropdown(false);
      }
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target as Node)) {
        setShowGroupDropdown(false);
      }
      if (openStatusDropdownId) {
        const ref = statusDropdownRefs.current.get(openStatusDropdownId);
        if (ref && !ref.contains(event.target as Node)) {
          setOpenStatusDropdownId(null);
        }
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openStatusDropdownId]);

  const handleMonthToggle = useCallback((monthValue: string) => {
    setSelectedMonths((prev) => {
      if (prev.includes(monthValue)) {
        return prev.filter((m) => m !== monthValue);
      }
      return [...prev, monthValue];
    });
  }, []);

  const handleSelectAllMonths = useCallback(() => {
    if (selectedMonths.length === MONTH_OPTIONS.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(MONTH_OPTIONS.map((m) => m.value));
    }
  }, [selectedMonths.length]);

  const handleStatusToggle = useCallback((statusValue: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(statusValue)) {
        return prev.filter((s) => s !== statusValue);
      }
      return [...prev, statusValue];
    });
  }, []);

  // The default selection omits `deleted`, so "all options selected" is not the resting state
  // here as it is for months and groups. Keying off "anything selected" keeps Clear All
  // reachable from that default.
  const handleSelectAll = useCallback(() => {
    if (selectedStatuses.length > 0) {
      setSelectedStatuses([]);
    } else {
      setSelectedStatuses(ADMIN_STATUS_OPTIONS.map((s) => s.value));
    }
  }, [selectedStatuses.length]);

  const handleGroupToggle = useCallback((groupValue: string) => {
    setExcludedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupValue)) next.delete(groupValue);
      else next.add(groupValue);
      return next;
    });
  }, []);

  const handleSelectAllGroups = useCallback(() => {
    if (excludedGroups.size === 0) {
      setExcludedGroups(new Set(userTypes.map((g) => g.value)));
    } else {
      setExcludedGroups(new Set());
    }
  }, [excludedGroups.size, userTypes]);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const toggleSortOrder = useCallback(() => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const handleStatusChange = useCallback(
    (claimId: string, userEmail: string, currentStatus: string, newStatusKey: string) => {
      if (currentStatus === newStatusKey) {
        return;
      }
      const { status: newStatus, flag: newFlag } = resolveStatusOption(newStatusKey);
      setStatusChangeModal({
        claimId,
        userEmail,
        currentStatus,
        newStatus,
        newStatusKey,
        newFlag,
      });
    },
    [],
  );

  const handleConfirmStatusChange = useCallback(async () => {
    if (!statusChangeModal || !user?.email) {
      return;
    }

    try {
      await updateClaimStatusMutation.updateStatus({
        claimId: statusChangeModal.claimId,
        userEmail: statusChangeModal.userEmail,
        status: statusChangeModal.newStatus,
        flag: statusChangeModal.newFlag,
      });
      setStatusChangeModal(null);
      setSnackbar({
        open: true,
        message: `Claim status updated to ${statusChangeModal.newStatus}`,
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating claim status:', error);
      const msg = error instanceof Error ? error.message : null;
      setSnackbar({
        open: true,
        message: msg || 'Failed to update claim status. Please try again.',
        type: 'error',
      });
    }
  }, [
    statusChangeModal,
    user?.email,
    updateClaimStatusMutation,
    setSnackbar,
    setStatusChangeModal,
  ]);

  const handleCancelStatusChange = useCallback(() => {
    setStatusChangeModal(null);
  }, []);

  const toggleClaimSelection = useCallback(
    (key: string) => {
      setSelectedClaimIds((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else if (next.size < 50) next.add(key);
        return next;
      });
    },
    [setSelectedClaimIds],
  );

  const handleToggleSelectAllOnPage = useCallback(() => {
    const pageKeys = claims.map(claimRowKey);
    setSelectedClaimIds((prev) => {
      const allSelected = pageKeys.length > 0 && pageKeys.every((key) => prev.has(key));
      if (allSelected) {
        const next = new Set(prev);
        pageKeys.forEach((key) => next.delete(key));
        return next;
      }
      return new Set([...prev, ...pageKeys]);
    });
  }, [claims, setSelectedClaimIds]);

  const selectedClaimObjects = useMemo(
    () => claims.filter((c) => selectedClaimIds.has(claimRowKey(c))),
    [claims, selectedClaimIds],
  );

  const availableChangeStatusTargets = useMemo(
    () =>
      selectedClaimObjects.length === 0
        ? []
        : Object.keys(BULK_STATUS_TRANSITIONS).filter((target) =>
            selectedClaimObjects.every((c) =>
              BULK_STATUS_TRANSITIONS[target].includes(getEffectiveStatusKey(c.status, c.flag)),
            ),
          ),
    [selectedClaimObjects],
  );

  const targetStatusOptions = useMemo(
    () =>
      availableChangeStatusTargets
        .map((target) => ADMIN_STATUS_OPTIONS.find((o) => o.value === target))
        .filter((o): o is (typeof ADMIN_STATUS_OPTIONS)[number] => o != null),
    [availableChangeStatusTargets],
  );

  useEffect(() => {
    if (!availableChangeStatusTargets.includes(selectedTargetStatus)) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSelectedTargetStatus(availableChangeStatusTargets[0] ?? '');
    }
  }, [availableChangeStatusTargets, selectedTargetStatus]);

  const handleReceiptClick = useCallback(
    async (s3Key: string) => {
      const uniqueKey = s3Key;

      if (loadingReceipts.has(uniqueKey)) {
        return;
      }

      setLoadingReceipts((prev) => new Set(prev).add(uniqueKey));

      const win = window.open('about:blank', '_blank');
      if (win) win.opener = null;
      try {
        const downloadUrl = await getReceiptDownloadUrl(s3Key);
        if (win) win.location.href = downloadUrl;
      } catch (error) {
        if (win) win.close();
        console.error('Error fetching receipt URL:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load receipt. Please try again.',
          type: 'error',
        });
      } finally {
        setLoadingReceipts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uniqueKey);
          return newSet;
        });
      }
    },
    [loadingReceipts, setSnackbar, setLoadingReceipts],
  );

  const shouldShowNoResults = currentPage === 1 && claims.length === 0 && !loading;

  const hasNextPage = data?.nextCursor != null;
  const totalPages = cursorHistory.length;

  const allOnPageSelected =
    claims.length > 0 && claims.every((c) => selectedClaimIds.has(claimRowKey(c)));
  const someOnPageSelected = claims.some((c) => selectedClaimIds.has(claimRowKey(c)));

  const columns: TableColumn<(typeof claims)[0]>[] = [
    {
      header: (
        <input
          type="checkbox"
          checked={allOnPageSelected}
          ref={(el) => {
            if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
          }}
          disabled={claims.length === 0}
          onChange={handleToggleSelectAllOnPage}
          className="size-4 cursor-pointer rounded border-2 border-gray-400 accent-red-600 focus:ring-red-500 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100"
          aria-label="Select all claims on this page"
        />
      ),
      headerClassName: 'w-[48px] text-center',
      className: 'w-[48px] text-center',
      accessor: (claim) => {
        const key = claimRowKey(claim);
        const isChecked = selectedClaimIds.has(key);
        const atCap = selectedClaimIds.size >= 50;
        return (
          <input
            type="checkbox"
            checked={isChecked}
            disabled={!isChecked && atCap}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleClaimSelection(key)}
            className="size-4 cursor-pointer rounded border-2 border-gray-400 accent-red-600 focus:ring-red-500 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100"
          />
        );
      },
    },
    {
      header: 'Note',
      accessor: (claim) => (
        <div className="truncate text-gray-900" title={claim.details}>
          {claim.details || <span className="text-gray-400">—</span>}
        </div>
      ),
      className: 'max-w-[200px]',
      headerClassName: 'max-w-[200px] w-[200px]',
      skeletonRenderer: () => <div className="h-4 w-32 rounded bg-gray-200"></div>,
    },
    {
      header: 'X-Teamer',
      accessor: (claim) => (
        <div className="line-clamp-4 font-semibold wrap-break-word">
          {claim.userName || claim.userEmail}
        </div>
      ),
      className: 'text-gray-900 max-w-[160px]',
      headerClassName: 'w-[200px] max-w-[200px]',
      skeletonRenderer: () => (
        <div className="line-clamp-4 h-4 w-24 rounded bg-gray-200 wrap-break-word"></div>
      ),
    },
    {
      header: 'Group',
      accessor: (claim) => (
        <div className="truncate text-sm text-gray-700" title={claim.userType ?? ''}>
          {claim.userType ?? <span className="text-gray-400">—</span>}
        </div>
      ),
      className: 'w-[200px] max-w-[200px]',
      headerClassName: 'w-[200px] max-w-[200px]',
      skeletonRenderer: () => <div className="h-4 w-20 rounded bg-gray-200"></div>,
    },
    {
      header: 'Category',
      accessor: (claim) => {
        const cat = categories.find((c) => c.id === claim.category);
        if (!cat?.icon) return <span className="text-gray-400">—</span>;
        const Icon = cat.icon;
        return (
          <span
            className={`inline-flex size-8 items-center justify-center rounded-md ${cat.iconBg}`}
            title={cat.name}
          >
            <Icon size={16} className={cat.iconText} />
          </span>
        );
      },
      className: 'text-center w-[48px]',
      headerClassName: 'w-[48px]',
      skeletonRenderer: () => <div className="size-8 rounded-md bg-gray-200"></div>,
    },
    {
      header: 'Attachment',
      accessor: (claim) => {
        if (!claim.receipt) {
          return <span className="text-gray-400">—</span>;
        }

        const isLoading = loadingReceipts.has(claim.receipt);

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleReceiptClick(claim.receipt);
            }}
            className={`text-xl text-red-600 hover:text-red-800 ${
              isLoading ? 'cursor-wait opacity-50' : 'cursor-pointer'
            }`}
            title={isLoading ? 'Loading...' : 'View receipt'}
            disabled={isLoading}
          >
            {isLoading ? '⏳' : '📎'}
          </button>
        );
      },
      className: 'text-center whitespace-nowrap',
      headerClassName: 'w-[120px] max-w-[120px]',
      skeletonRenderer: () => <div className="size-5 rounded bg-gray-200"></div>,
    },
    {
      header: 'Amount',
      accessor: (claim) => `$${claim.amount.toFixed(2)}`,
      className: 'whitespace-nowrap text-gray-900',
      headerClassName: 'w-[120px] max-w-[120px]',
      skeletonRenderer: () => <div className="h-4 w-16 rounded bg-gray-200"></div>,
    },
    {
      header: 'Status',
      skeletonRenderer: () => <div className="mx-auto h-7 w-[160px] rounded bg-gray-200"></div>,
      accessor: (claim) => {
        const uniqueKey = claimRowKey(claim);
        const claimFlag = claim.flag;
        const validTransitions = getValidStatusTransitions(claim.status, claimFlag);
        const currentEffectiveKey = getEffectiveStatusKey(claim.status, claimFlag);
        return (
          <div
            ref={(el) => {
              if (el) {
                statusDropdownRefs.current.set(uniqueKey, el);
              } else {
                statusDropdownRefs.current.delete(uniqueKey);
              }
            }}
            className="relative inline-block"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!updateClaimStatusMutation.isPending) {
                  setOpenStatusDropdownId(openStatusDropdownId === uniqueKey ? null : uniqueKey);
                }
              }}
              data-testid="claim-status-button"
              className={`inline-block cursor-pointer ${updateClaimStatusMutation.isPending ? 'pointer-events-none opacity-50' : ''}`}
              disabled={updateClaimStatusMutation.isPending}
            >
              <StatusLabel status={claim.status} flag={claimFlag} className="w-[160px]">
                <ChevronDown size={12} className="shrink-0 opacity-50" />
              </StatusLabel>
            </button>
            {openStatusDropdownId === uniqueKey && (
              <div
                data-testid="status-dropdown"
                className="absolute top-full left-0 z-50 mt-1 w-full rounded-md border border-gray-200 bg-white text-wrap shadow-lg"
              >
                <ClaimStatusDropdown
                  currentEffectiveKey={currentEffectiveKey}
                  validTransitions={validTransitions}
                  onSelect={(optValue) => {
                    handleStatusChange(claim.id, claim.userEmail, currentEffectiveKey, optValue);
                    setOpenStatusDropdownId(null);
                  }}
                />
              </div>
            )}
          </div>
        );
      },
      className: 'whitespace-nowrap text-center w-[180px] min-w-[180px]',
      headerClassName: '!text-center w-[180px] min-w-[180px]',
    },
    {
      header: (
        <button
          onClick={toggleSortOrder}
          className="flex items-center gap-1 tracking-wider uppercase transition-colors hover:text-gray-700"
        >
          Date
          {sortOrder === 'desc' ? (
            <ChevronDown size={14} className="text-gray-400" />
          ) : (
            <ChevronUp size={14} className="text-gray-400" />
          )}
        </button>
      ),
      accessor: (claim) => {
        if (!claim.createdAt) return '—';
        const date = new Date(claim.createdAt);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${month}/${day}/${year}`;
      },
      className: 'whitespace-nowrap text-gray-500 w-[100px]',
      headerClassName: 'w-[100px]',
      skeletonRenderer: () => <div className="h-4 w-20 rounded bg-gray-200"></div>,
    },
  ];

  const emptyMessage = shouldShowNoResults ? (
    <>
      <div className="mb-4 text-6xl">📋</div>
      <h3 className="mb-2 text-lg font-medium text-gray-900">No claims found</h3>
      <p className="text-gray-600">Try adjusting your filter criteria.</p>
    </>
  ) : undefined;

  const isFilterOpen =
    openStatusDropdownId || showGroupDropdown || showStatusDropdown || showMonthDropdown;

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-lg bg-white shadow-md">
      <div className={`min-h-0 flex-1 ${isFilterOpen ? 'overflow-visible' : 'overflow-auto'}`}>
        <div className="border-b border-gray-200 p-6">
          <ClaimsFilters
            nameQuery={nameQuery}
            onNameQueryChange={setNameQuery}
            selectedMonths={selectedMonths}
            monthOptions={MONTH_OPTIONS}
            showMonthDropdown={showMonthDropdown}
            onToggleMonthDropdown={() => setShowMonthDropdown(!showMonthDropdown)}
            onSelectAllMonths={handleSelectAllMonths}
            onToggleMonth={handleMonthToggle}
            monthDropdownRef={monthDropdownRef}
            selectedStatuses={selectedStatuses}
            statusOptions={ADMIN_STATUS_OPTIONS}
            showStatusDropdown={showStatusDropdown}
            onToggleStatusDropdown={() => setShowStatusDropdown(!showStatusDropdown)}
            onSelectAllStatuses={handleSelectAll}
            onToggleStatus={handleStatusToggle}
            selectedGroups={selectedGroupValues}
            groupOptions={userTypes}
            showGroupDropdown={showGroupDropdown}
            onToggleGroupDropdown={() => setShowGroupDropdown(!showGroupDropdown)}
            onSelectAllGroups={handleSelectAllGroups}
            onToggleGroup={handleGroupToggle}
            dropdownRef={dropdownRef}
            groupDropdownRef={groupDropdownRef}
          />
        </div>

        <BulkActionBar
          selectedCount={selectedClaimIds.size}
          onClearSelection={() => setSelectedClaimIds(new Set())}
          targetStatusOptions={targetStatusOptions}
          selectedTargetStatus={selectedTargetStatus}
          onTargetStatusChange={setSelectedTargetStatus}
          onChangeStatusClick={() => setBulkStatusTarget(selectedTargetStatus)}
        />

        {error ? (
          <div className="p-8 text-center">
            <div className="mb-2 text-lg font-medium text-red-500">Error loading claims</div>
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <Table
            columns={columns}
            data={claims}
            loading={loading}
            skeletonRows={pageSize}
            emptyMessage={emptyMessage}
            getRowKey={(claim) => claimRowKey(claim)}
            getRowProps={(claim) => ({
              'data-testid': 'admin-claim-row',
              'data-claim-details': claim.details ?? '',
            })}
            onRowClick={(claim) => setActiveClaimId(claim.id)}
            isLoading={refetching || updateClaimStatusMutation.isPending}
            allowOverflow={!!openStatusDropdownId}
          />
        )}
      </div>
      <div className="relative z-10 mt-auto bg-white">
        <Pagination
          currentPage={currentPage}
          hasMore={hasNextPage}
          hasPrevious={currentPage > 1}
          pageSize={pageSize}
          currentItemsCount={claims.length}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSizeOptions={[25, 50, 100]}
          onPageSizeChange={setPageSize}
          actions={
            <div ref={exportMenuRef} className="relative">
              {showExportMenu && (
                <div className="absolute bottom-full left-0 z-40 mb-2 w-56 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                  <button
                    type="button"
                    disabled={selectedClaimIds.size === 0}
                    onClick={() => {
                      setExportMode('selected');
                      setShowExportMenu(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 hover:disabled:bg-transparent"
                  >
                    Export selected ({selectedClaimIds.size})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExportMode('all');
                      setShowExportMenu(false);
                    }}
                    className="w-full border-t border-gray-100 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Export all matching filters
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowExportMenu((prev) => !prev)}
                aria-label="Export claims to CSV"
                aria-expanded={showExportMenu}
                title="Export claims to CSV"
                className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white shadow-sm transition-colors hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-hidden"
              >
                <Download size={18} strokeWidth={2.5} />
              </button>
            </div>
          }
        />
      </div>

      {statusChangeModal && (
        <ConfirmChangeModal
          from={statusChangeModal.currentStatus}
          to={statusChangeModal.newStatusKey}
          onConfirm={handleConfirmStatusChange}
          onCancel={handleCancelStatusChange}
          isLoading={updateClaimStatusMutation.isPending}
        />
      )}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ open: false, message: '', type: 'error' })}
      />
      {activeClaimForDetails && (
        <AdminClaimDetailsModal
          claim={activeClaimForDetails}
          onClose={() => setActiveClaimId(null)}
        />
      )}
      {bulkStatusTarget &&
        (() => {
          const modalClaims = eligibleForTarget(selectedClaimObjects, bulkStatusTarget);
          const targetLabel =
            ADMIN_STATUS_OPTIONS.find((o) => o.value === bulkStatusTarget)?.label ??
            bulkStatusTarget;
          const successMessage =
            bulkStatusTarget === 'approved'
              ? `${modalClaims.length} claims approved successfully`
              : `${modalClaims.length} claims updated to ${targetLabel} successfully`;
          return (
            <BulkApproveModal
              selectedClaims={modalClaims}
              targetStatus={bulkStatusTarget}
              onClose={() => setBulkStatusTarget(null)}
              onFullSuccess={() => {
                setSelectedClaimIds(new Set());
                setBulkStatusTarget(null);
                setSnackbar({
                  open: true,
                  message: successMessage,
                  type: 'success',
                });
              }}
              onPartialOrTotalFailure={(retainedClaimIds) => {
                const retainedKeys = new Set(
                  modalClaims
                    .filter((c) => retainedClaimIds.includes(c.claimId))
                    .map((c) => claimRowKey({ userEmail: c.userEmail, id: c.claimId })),
                );
                setSelectedClaimIds(retainedKeys);
              }}
            />
          );
        })()}
      {exportMode && (
        <ExportClaimsModal
          selectedClaims={selectedClaimObjects}
          mode={exportMode}
          filterParams={exportFilterParams}
          filtersMatchNothing={filtersMatchNothing}
          onClose={() => setExportMode(null)}
        />
      )}
    </div>
  );
}

export default ClaimsTab;
